import { RefetchQueriesInclude } from '@apollo/client';
import {
  HStack,
  Box,
  Menu,
  MenuButton,
  MenuList,
  MenuGroup,
  MenuItem,
  Divider,
} from '@chakra-ui/react';
import { Button, FileIcon, TruncatableText, IconButton } from 'Atoms';
import { AttachmentContainer } from 'Molecules/Attachment';
import { ChevronDownIcon } from 'Tokens/Icons/Direction';
import { AddIcon, RemoveIcon } from 'Tokens/Icons/Function';
import { CheckIcon } from 'Tokens/Icons/Status';
import { AttachmentBox } from 'models';
import { useEffect, useMemo, useState } from 'react';
import { getNameExtension } from 'utils/files';

// Allow up to 5 attachments
const MAX_ALLOWED_ATTACHMENTS = 5;

export const DisclosureRequirementAttachment = ({
  attachmentBox,
  onAttachmentDrawerOpen,
  refetchQueries,
}: {
  attachmentBox?: AttachmentBox;
  onAttachmentDrawerOpen: () => void;
  refetchQueries: RefetchQueriesInclude;
}) => {
  const attachments = useMemo(() => {
    return attachmentBox?.attachments ?? [];
  }, [attachmentBox]);

  return (
    <>
      {!attachments.length ? (
        <Button
          px={0}
          variant="ghost"
          size="sm"
          color="text.selected"
          _hover={{ bg: 'bg.hover', color: 'text.selected' }}
          _active={{ bg: 'bg.pressed' }}
          leftIcon={<AddIcon boxSize="16px" color="inherit" />}
          onClick={onAttachmentDrawerOpen}
        >
          Add document
        </Button>
      ) : (
        <AttachmentContainer
          openAttachmentDrawer={onAttachmentDrawerOpen}
          attachmentBox={attachmentBox}
          refetchQueries={refetchQueries}
          size="sm"
          isDisabled={attachments.length >= MAX_ALLOWED_ATTACHMENTS}
          addAttachmentTooltip={
            attachments.length >= MAX_ALLOWED_ATTACHMENTS
              ? `You can only add up to ${MAX_ALLOWED_ATTACHMENTS} documents`
              : `Add another document (max ${MAX_ALLOWED_ATTACHMENTS})`
          }
        />
      )}
    </>
  );
};

export const PolicyAttachment = ({
  attachments,
  selectedPolicyFileIds,
  setSelectedPolicyFileIds,
}: {
  attachments: AttachmentBox['attachments'];
  selectedPolicyFileIds: string[];
  setSelectedPolicyFileIds: (fileIds: string[]) => void;
}) => {
  const [pendingSelectedIds, setPendingSelectedIds] = useState<string[]>(
    selectedPolicyFileIds ?? []
  );

  const selectedAttachments = useMemo(
    () =>
      attachments?.filter((attachment) => selectedPolicyFileIds.includes(attachment.file?.id)) ??
      [],
    [attachments, selectedPolicyFileIds]
  );

  const getExtension = (fileName?: string) => getNameExtension(fileName ?? '').extension;

  const handleMenuItemClick = (fileId: string) => {
    if (pendingSelectedIds.includes(fileId)) {
      setPendingSelectedIds(pendingSelectedIds.filter((id) => id !== fileId));
    } else if (pendingSelectedIds.length < MAX_ALLOWED_ATTACHMENTS) {
      setPendingSelectedIds([...pendingSelectedIds, fileId]);
    }
  };

  const handleUseFiles = () => {
    setSelectedPolicyFileIds(pendingSelectedIds);
  };

  const handleRemove = (fileId: string) => {
    setSelectedPolicyFileIds(selectedPolicyFileIds.filter((id) => id !== fileId));
    setPendingSelectedIds(pendingSelectedIds.filter((id) => id !== fileId));
  };

  useEffect(() => {
    setPendingSelectedIds(selectedPolicyFileIds);
  }, [selectedPolicyFileIds]);

  return (
    <>
      {selectedPolicyFileIds.length === 0 ? (
        <Menu gutter={2} closeOnSelect={false}>
          {({ onClose }) => (
            <>
              <MenuButton
                as={Button}
                variant="ghost"
                color="text.selected"
                _hover={{ bg: 'bg.hover', color: 'text.selected' }}
                _active={{ bg: 'bg.pressed' }}
                rightIcon={<ChevronDownIcon boxSize="16px" color="inherit" />}
                size="sm"
                px={0}
              >
                Select policies
              </MenuButton>
              <MenuList p="8px" w="280px">
                <MenuGroup p="8px">
                  {attachments?.map((attachment) => {
                    const fileId = attachment.file?.id;
                    const isSelected = pendingSelectedIds.includes(fileId);
                    const isDisabled =
                      !isSelected && pendingSelectedIds.length >= MAX_ALLOWED_ATTACHMENTS;
                    return (
                      <MenuItem
                        key={fileId}
                        p="8px"
                        m="0px"
                        w="100%"
                        isDisabled={isDisabled}
                        onClick={() => handleMenuItemClick(fileId)}
                      >
                        <HStack
                          w="100%"
                          justifyContent="space-between"
                          color={isSelected ? 'text.selected' : ''}
                        >
                          <HStack>
                            <TruncatableText
                              color="inherit"
                              text={attachment.file.title}
                              variant="bodyStrong"
                            />
                          </HStack>
                          {isSelected && <CheckIcon color="inherit" />}
                        </HStack>
                      </MenuItem>
                    );
                  })}
                </MenuGroup>
                <Divider color="border.decorative" my="8px" ml="-8px" pr="16px" />
                <Box w="100%">
                  <Button
                    isDisabled={pendingSelectedIds.length === 0}
                    variant="primary"
                    w="100%"
                    onClick={() => {
                      handleUseFiles();
                      onClose();
                    }}
                  >
                    Use {pendingSelectedIds.length > 1 ? 'these files' : 'this file'}
                  </Button>
                </Box>
              </MenuList>
            </>
          )}
        </Menu>
      ) : (
        <Menu gutter={2} closeOnSelect={false}>
          {({ onClose }) => (
            <>
              <HStack spacing="8px" flexWrap="wrap">
                {selectedAttachments.map((attachment) => {
                  const file = attachment.file;
                  const extension = getExtension(file?.storageFile?.name ?? '');
                  return (
                    <HStack
                      key={file.id}
                      h="28px"
                      bg="bg.default"
                      justifyContent="center"
                      alignItems="center"
                      spacing="6px"
                      p="4px"
                      border="1px solid"
                      borderColor="border.decorative"
                      borderRadius="5px"
                      maxW="200px"
                    >
                      <Box w="16.67px">
                        <FileIcon extension={extension} />
                      </Box>
                      <TruncatableText text={file?.title} variant="bodyStrong"></TruncatableText>
                      <IconButton
                        variant="ghost"
                        aria-label="Remove"
                        size="xs"
                        icon={<RemoveIcon color="inherit" />}
                        onClick={() => handleRemove(file.id)}
                      />
                    </HStack>
                  );
                })}
                <MenuButton
                  as={IconButton}
                  aria-label="Add Attachment"
                  icon={<AddIcon color="inherit" />}
                  variant="ghost"
                  isDisabled={
                    attachments.length === 0 || pendingSelectedIds.length >= MAX_ALLOWED_ATTACHMENTS
                  }
                  size="sm"
                  tooltipLabel={
                    selectedAttachments.length >= MAX_ALLOWED_ATTACHMENTS
                      ? `You can only select up to ${MAX_ALLOWED_ATTACHMENTS} files`
                      : 'Add another file'
                  }
                />
              </HStack>
              <MenuList p="8px" w="280px">
                <MenuGroup p="8px">
                  {attachments?.map((attachment) => {
                    const fileId = attachment.file?.id;
                    const isSelected = pendingSelectedIds.includes(fileId);
                    const isDisabled =
                      !isSelected && pendingSelectedIds.length >= MAX_ALLOWED_ATTACHMENTS;
                    return (
                      <MenuItem
                        key={fileId}
                        p="8px"
                        m="0px"
                        w="100%"
                        isDisabled={isDisabled}
                        onClick={() => handleMenuItemClick(fileId)}
                      >
                        <HStack
                          w="100%"
                          justifyContent="space-between"
                          color={isSelected ? 'text.selected' : ''}
                        >
                          <HStack>
                            <TruncatableText
                              color="inherit"
                              text={attachment.file.title}
                              variant="bodyStrong"
                            />
                          </HStack>
                          {isSelected && <CheckIcon color="inherit" />}
                        </HStack>
                      </MenuItem>
                    );
                  })}
                </MenuGroup>
                <Divider color="border.decorative" my="8px" ml="-8px" pr="16px" />
                <Box w="100%">
                  <Button
                    isDisabled={pendingSelectedIds.length === 0}
                    variant="primary"
                    w="100%"
                    onClick={() => {
                      handleUseFiles();
                      onClose();
                    }}
                  >
                    Use {pendingSelectedIds.length > 1 ? 'these files' : 'this file'}
                  </Button>
                </Box>
              </MenuList>
            </>
          )}
        </Menu>
      )}
    </>
  );
};
